import api from './api';

export type DraftStatus = 'pending' | 'sending' | 'sent' | 'discarded';

/** One file on an email; the url comes from our upload flow or the resource library. */
export interface EmailAttachment {
  url: string;
  type: 'image' | 'document' | 'video' | 'audio';
  name: string;
  mime_type: string;
}

/** The draft's state after an action; `text` is what went out when sent. */
export interface DraftView {
  status: DraftStatus;
  text?: string;
  html?: string | null;
  attachment?: EmailAttachment | null;
  media?: Record<string, unknown>;
}

/** ok: the server answered with the draft's state (view); otherwise message is its reason, possibly empty. */
export interface DraftCallResult {
  ok: boolean;
  view?: DraftView;
  message?: string;
  status?: number;
}

async function call(request: Promise<any>): Promise<DraftCallResult> {
  const response = await request;
  if (response.success) return { ok: true, view: response.data as DraftView };
  // detail is a string, or {message, status} on a 409 (the draft is no longer pending).
  const detail = response.data?.detail;
  const message = typeof detail === 'string' ? detail : detail?.message;
  return { ok: false, status: response.status, message: typeof message === 'string' ? message : '' };
}

const base = (messageId: string) => `messaging/messages/${messageId}/draft`;

/** Without text the server sends what is saved (text, html and attachment); text is the legacy plain path. */
export const approveDraft = (messageId: string, text?: string) => call(
  api.post(`${base(messageId)}/approve`, text === undefined ? {} : { text }),
);

export const discardDraft = (messageId: string) => call(api.post(`${base(messageId)}/discard`, {}));

/** Saves an edit without sending it; only a pending draft can be edited (409 otherwise). */
export const saveDraft = (
  messageId: string,
  body: { html: string; attachment: EmailAttachment | null },
) => call(api.put(base(messageId), body));

export interface EmailReply {
  /** Plain version for the required `message` field; the server derives its own from html. */
  text: string;
  html: string;
  attachment: EmailAttachment | null;
}

/** send-message body for a Gmail reply: text, html and at most one file in a single email. */
export const emailReplyPayload = (sessionId: string, reply: EmailReply, trackId: string) => ({
  session_id: sessionId,
  message: reply.text,
  html: reply.html,
  track_id: trackId,
  ...(reply.attachment ? {
    media_url: reply.attachment.url,
    media_type: reply.attachment.type,
    media_name: reply.attachment.name,
    media_mime_type: reply.attachment.mime_type,
  } : {}),
});

/** send-message answers `success: true` once the email left; there is no `status` field. */
export const emailReplySent = (res: any) => res?.success === true && res.data?.success === true;
